export type DeltaOperation = 'create' | 'update' | 'delete';

export type RbacDeltaEntity = 'user' | 'workspace' | 'member';

export type WorkspaceResourceDeltaEntity =
    | 'investment'
    | 'pipeline'
    | 'pipelineColumn'
    | 'memo'
    | 'dashboard'
    | 'list'
    | 'folder'
    | 'team'
    | 'collaborator';

export type NestedResourceDeltaEntity = 'report' | 'reportComponent' | 'listView' | 'listViewColumn';

export type RealtimeDeltaEntity = 'inbox';

export type CollaborationDeltaEntity = 'resourceActivity' | 'resourceComment';

export type LocalDeltaEntity =
    | RbacDeltaEntity
    | WorkspaceResourceDeltaEntity
    | NestedResourceDeltaEntity
    | RealtimeDeltaEntity
    | CollaborationDeltaEntity;

export type DeltaPacket<
    TEntity extends string = LocalDeltaEntity,
    TPayload = unknown,
    TOperation extends string = DeltaOperation,
> = {
    v: 1;
    type: 'delta:packet';
    entity: TEntity;
    operation: TOperation;
    entityId: string;
    workspaceId?: string;
    payload?: TPayload;
    actorUserId?: string;
    timestamp: number;
};

export type LocalDeltaPacket = DeltaPacket<string, unknown, string>;

type CreateLocalDeltaPacketInput<TEntity extends string, TPayload, TOperation extends string> = Omit<
    DeltaPacket<TEntity, TPayload, TOperation>,
    'v' | 'type' | 'timestamp'
> & {
    timestamp?: number;
};

const CHANNEL_NAME = 'countercyclical:delta-packets:v1';

let publishChannel: BroadcastChannel | null = null;

const getPublishChannel = () => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
        return null;
    }

    publishChannel ??= new BroadcastChannel(CHANNEL_NAME);

    return publishChannel;
};

export const publishLocalDeltaPacket = (packet: LocalDeltaPacket) => {
    getPublishChannel()?.postMessage(packet);
};

export const createLocalDeltaPacket = <
    TEntity extends string = LocalDeltaEntity,
    TPayload = unknown,
    TOperation extends string = DeltaOperation,
>(
    input: CreateLocalDeltaPacketInput<TEntity, TPayload, TOperation>,
): DeltaPacket<TEntity, TPayload, TOperation> => ({
    v: 1,
    type: 'delta:packet',
    ...input,
    timestamp: input.timestamp ?? Date.now(),
});

export const publishLocalEntityDelta = <
    TEntity extends string = LocalDeltaEntity,
    TPayload = unknown,
    TOperation extends string = DeltaOperation,
>(
    input: CreateLocalDeltaPacketInput<TEntity, TPayload, TOperation>,
) => {
    const packet = createLocalDeltaPacket(input);

    publishLocalDeltaPacket(packet);

    return packet;
};

export const subscribeLocalDeltaPackets = (handler: (packet: LocalDeltaPacket) => void) => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
        return () => {};
    }

    const channel = new BroadcastChannel(CHANNEL_NAME);

    const listener = (event: MessageEvent<LocalDeltaPacket>) => {
        if (event.data?.type === 'delta:packet') {
            handler(event.data);
        }
    };

    channel.addEventListener('message', listener);

    return () => {
        channel.removeEventListener('message', listener);
        channel.close();
    };
};
